import { Card, Avatar, Chip, Icon, Button, TextField, InputAdornment, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import PageLayout from "examples/LayoutContainers/PageLayout";
import DataTable from "examples/Tables/DataTable";
import MDProgressCircular from 'components/MDProgressCircular'
import { useAppServices } from "hook/services";
import { useAgencyInfo } from "context/agency";

function CouponHistory() {
    const { promoCodeId } = useParams();
    const navigate = useNavigate();
    const AppService = useAppServices();
    const [agency] = useAgencyInfo();
    const [loading, setLoading] = useState(true);
    const [history, setHistory] = useState([]);
    const [couponInfo, setCouponInfo] = useState(null);
    const [search, setSearch] = useState("");
    const [dateRange, setDateRange] = useState([null, null]);

    const fetchHistory = async () => {
        setLoading(true);
        const { response } = await AppService.coupons.getCouponHistory({
            query: `promoCodeId=${promoCodeId}&agency_id=${agency._id}`,
        });
        if (response && response.success) {
            setHistory(response.data || []);
            setCouponInfo(response.coupon);
        }
        setLoading(false);
    };

    useEffect(() => {
        if (agency?._id && promoCodeId) {
            fetchHistory();
        }
    }, [agency, promoCodeId]);

    const getInitials = (name) => {
        if (!name) return "U";
        const parts = name.trim().split(" ");
        if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        return parts[0][0].toUpperCase();
    };

    const getRandomColor = (name) => {
        const colors = ["#4CAF50", "#2196F3", "#FF9800", "#9C27B0", "#E91E63", "#00BCD4"];
        let hash = 0;
        const str = name || "Unknown";
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const handleExportCSV = () => {
        if (!history || history.length === 0) return;

        const headers = ["Customer", "SaaS Plan", "Purchase Date", "Status"];
        const csvRows = [headers.join(",")];

        history.forEach((item) => {
            const rawName = item.name || "";
            const namePart = rawName.split('@')[0];
            const displayName = namePart.charAt(0).toUpperCase() + namePart.slice(1).toLowerCase();

            const date = new Date(item.redeemedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            }) + " at " + new Date(item.redeemedAt).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
            });

            // Escape quotes and wrap in quotes to handle commas in data
            const row = [
                `"${displayName}"`,
                `"${item.planName || "Starter"}"`,
                `"${date}"`,
                `"${item.status || "Completed"}"`
            ];
            csvRows.push(row.join(","));
        });

        const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `coupon_history_${couponInfo?.code || "data"}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const tableData = {
        columns: [
            {
                Header: "Customer",
                accessor: "name",
                width: "30%",
                Cell: ({ row }) => (
                    <SoftBox display="flex" alignItems="center">
                        <Avatar
                            sx={{
                                bgcolor: getRandomColor(row.original.name),
                                width: 32,
                                height: 32,
                                fontSize: "0.875rem",
                                mr: 2
                            }}
                        >
                            {getInitials(row.original.name)}
                        </Avatar>
                        <SoftTypography variant="button" fontWeight="medium">
                            {row.original.name}
                        </SoftTypography>
                    </SoftBox>
                )
            },
            { Header: "SaaS Plan", accessor: "planName", width: "25%" },
            { Header: "Purchase Date", accessor: "redeemedAt", width: "25%" },
            {
                Header: "Status",
                accessor: "status",
                width: "20%",
                Cell: ({ value }) => (
                    <Chip
                        label={value || "Completed"}
                        size="small"
                        sx={{
                            backgroundColor: value === "Pending" ? "#FFF1C2" : "#eaf6ee",
                            color: value === "Pending" ? "#E5A000" : "#67ac5c",
                            fontWeight: "bold",
                            borderRadius: "16px",
                            border: `1px solid ${value === "Pending" ? "#f1e3b5" : "#cae7d6"}`,
                            textTransform: "capitalize"
                        }}
                    />
                )
            },
        ],
        rows: history.filter((item) => {
            const matchesSearch = search === "" ||
                (item.name || "").toLowerCase().includes(search.toLowerCase()) ||
                (item.planName || "").toLowerCase().includes(search.toLowerCase());

            let matchesDate = true;
            if (dateRange && dateRange[0] && dateRange[1]) {
                const itemDate = new Date(item.redeemedAt);
                const startDate = new Date(dateRange[0]);
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(dateRange[1]);
                endDate.setHours(23, 59, 59, 999);
                matchesDate = itemDate >= startDate && itemDate <= endDate;
            }

            return matchesSearch && matchesDate;
        }).map((item) => {
            const rawName = item.name || "";
            const namePart = rawName.split('@')[0];
            const displayName = namePart.charAt(0).toUpperCase() + namePart.slice(1).toLowerCase();

            return {
                name: displayName,
                planName: item.planName || "Starter",
                redeemedAt: new Date(item.redeemedAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                }) + " at " + new Date(item.redeemedAt).toLocaleTimeString('en-US', {
                    hour: '2-digit',
                    minute: '2-digit'
                }),
                status: item.status || "Completed",
            };
        }),
    };

    return (
        <PageLayout>
            <SoftBox pt={3} pb={3} px={3}>
                <SoftBox display="flex" alignItems="center" mb={1}>
                    <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <SoftTypography variant="h4" fontWeight="bold">
                        Orders {couponInfo?.name ? `- ${couponInfo.name}` : ""}
                    </SoftTypography>
                    <SoftBox ml="auto">
                        <SoftButton variant="outlined" color="dark" size="small" startIcon={<UploadFileIcon />} onClick={handleExportCSV}>
                            Export as CSV
                        </SoftButton>
                    </SoftBox>
                </SoftBox>
                <SoftTypography variant="button" color="text" fontWeight="regular" sx={{ ml: 6, display: "block", mt: -1.5, mb: 3 }}>
                    Track the history usage of a coupon {couponInfo?.code ? `(${couponInfo.code})` : ""}
                </SoftTypography>

                <SoftBox display="flex" alignItems="center" mb={3} gap={2}>
                    <Flatpickr
                        options={{
                            mode: "range",
                            dateFormat: "Y-m-d",
                        }}
                        value={dateRange}
                        onChange={(date) => setDateRange(date)}
                        render={({ defaultValue, value, ...props }, ref) => (
                            <TextField
                                {...props}
                                inputRef={ref}
                                placeholder="Start Date  →  End Date"
                                size="small"
                                sx={{ bgcolor: "white", borderRadius: "8px", width: "220px" }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Icon>calendar_today</Icon>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                    <SoftBox ml="auto" display="flex" gap={2}>
                        <TextField
                            placeholder="Search"
                            size="small"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{ bgcolor: "white", borderRadius: "8px", width: "200px" }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {/* <SoftButton variant="outlined" color="dark" size="small" startIcon={<FilterListIcon />}>
                            Filters
                        </SoftButton> */}
                    </SoftBox>
                </SoftBox>

                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: "12px", boxShadow: "0px 2px 6px rgba(0,0,0,0.05)" }}>
                            <SoftBox pt={1} px={0}>
                                {loading ? (
                                    <div style={{ height: "75vh", width: '100%', display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: "16px" }}>
                                        <MDProgressCircular />
                                        <p>Please hold on while we fetch your data...</p>
                                    </div>
                                ) : history.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        canSearch={false}
                                        entriesPerPage={false}
                                        showTotalEntries={false}
                                    />
                                ) : (
                                    <SoftBox py={6} textAlign="center">
                                        <SoftTypography variant="body2" color="text">
                                            No redemptions found for this coupon.
                                        </SoftTypography>
                                    </SoftBox>
                                )}
                            </SoftBox>
                        </Card>
                    </Grid>
                </Grid>
            </SoftBox>
        </PageLayout>
    );
}

export default CouponHistory;
